import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Building2, ArrowRight } from 'lucide-react';
import { getLocationVisuals, FALLBACK_VISUALS } from '../lib/locationVisuals';
import { getLocationCopy } from '../lib/locationDisplay';

export default function LocationCard({ location }) {
  const { id, name, region, nearby_bases = [] } = location;
  const { t, locale } = useLanguage();

  const visuals = getLocationVisuals(name, locale);
  const locationCopy = getLocationCopy(location, locale);
  const [imgSrc, setImgSrc] = useState(visuals.cardImage);

  return (
    <Link
      to={`/locations/${id}`}
      className="group block glass-card overflow-hidden transition-all hover:border-signal-blue/30 hover:shadow-md"
      data-testid={`location-card-${id}`}
    >
      {/* Hero Image */}
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={imgSrc}
          alt={name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={() => setImgSrc(visuals.fallback || FALLBACK_VISUALS.cardImage)}
        />

        {/* Dark gradient overlay — fades to dark at bottom for text legibility */}
        <div className="hero-overlay absolute inset-0" />

        {/* City name + tagline over the gradient */}
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="font-heading text-xl font-bold text-white mb-0.5 drop-shadow">
            {name}
          </h3>
          <div className="flex items-center gap-1 text-slate-300 text-xs mb-1.5">
            <MapPin className="w-3 h-3" />
            <span>{region || t('locations.germany', 'Germany')}</span>
          </div>
          {visuals.tagline && (
            <p className="text-slate-200 text-xs leading-snug line-clamp-2 drop-shadow">
              {visuals.tagline}
            </p>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="p-4">
        {locationCopy.overview && (
          <p className="text-slate-600 text-sm line-clamp-2 mb-3">{locationCopy.overview}</p>
        )}

        {/* Local flavor chips */}
        {visuals.localFlavor?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {visuals.localFlavor.slice(0, 3).map((label, i) => (
              <span
                key={i}
                className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full border border-slate-200"
              >
                {label}
              </span>
            ))}
          </div>
        )}

        {/* Nearby Bases */}
        {nearby_bases.length > 0 && (
          <div className="border-t border-slate-200 pt-3">
            <p className="label-tactical mb-2">{t('locations.nearby_bases', 'Nearby Bases')}</p>
            <div className="flex flex-wrap gap-1">
              {nearby_bases.slice(0, 2).map((base, i) => (
                <span
                  key={i}
                  className="flex items-center gap-1 px-2 py-1 bg-slate-800 text-slate-200 text-xs rounded-sm"
                >
                  <Building2 className="w-3 h-3" />
                  {base}
                </span>
              ))}
              {nearby_bases.length > 2 && (
                <span className="px-2 py-1 text-slate-500 text-xs">
                  +{nearby_bases.length - 2} {t('common.more', 'more')}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Explore link */}
        <div className="flex items-center gap-1 text-signal-blue text-sm mt-3 group-hover:gap-2 transition-all">
          <span>{t('locations.explore', 'Explore')} {name}</span>
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
}
